import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { CheckboxField, Flex, SidePanel, Text } from "@wayflyer/flyui";
import type { BankAccount, BankConnectionSchema, FilterBankAccountsParams } from "bankconnect";
import { getBankconnectUrls } from "bankconnect";
import { useReducer, useState } from "react";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { invalidateBankingSourceQueries, useSyncAccountGroups } from "../../../../queries/bank-connect/queries";
import { MESSAGES } from "../message-definitions";

const buildFilterStateFromConnections = (connections: BankConnectionSchema[]): FilterBankAccountsParams => {
  const connectionsMap: FilterBankAccountsParams = { connections: {} };
  for (const connection of connections) {
    connectionsMap.connections[connection.id] = { accounts: {} };
    for (const account of connection.accounts || []) {
      connectionsMap.connections[connection.id].accounts[account.id] = {
        enabled: connection.accountsSelected ? account.pull_enabled : true,
      };
    }
  }
  return connectionsMap;
};

const filterBankAccountsParamsReducer = (
  state: FilterBankAccountsParams,
  action: { connection: BankConnectionSchema; account: BankAccount; selected: boolean }
): FilterBankAccountsParams => ({
  connections: {
    ...state.connections,
    [action.connection.id]: {
      accounts: {
        ...state.connections[action.connection.id].accounts,
        [action.account.id]: { enabled: action.selected },
      },
    },
  },
});

const hasSelectedAccount = (state: FilterBankAccountsParams): boolean =>
  Object.values(state.connections).some((connectionAccountParams) =>
    Object.values(connectionAccountParams.accounts).some((bankParams) => bankParams.enabled)
  );

export interface ConfirmAccountsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  bankConnections: BankConnectionSchema[];
}

export const ConfirmAccountsPanel = ({ isOpen, onClose, bankConnections }: ConfirmAccountsPanelProps) => {
  const intl = useIntl();
  const client = useAxios();
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModal();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { mutateAsync: syncAccountGroups } = useSyncAccountGroups(customerDomain);
  const [saving, setSaving] = useState(false);

  const [selectedAccounts, dispatchSelectedAccounts] = useReducer(
    filterBankAccountsParamsReducer,
    bankConnections,
    (connections: BankConnectionSchema[]) => buildFilterStateFromConnections(connections)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.post<void>(getBankconnectUrls(customerDomain).filterAccounts, selectedAccounts);
      await syncAccountGroups();
      await invalidateBankingSourceQueries(queryClient, customerDomain);
      onClose();
    } catch (error: any) {
      openErrorModal({ variant: "submit-error", error });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel
      open={isOpen}
      isModal={false}
      closeOnClickElseWhere={false}
      onClose={onClose}
      title={intl.formatMessage(MESSAGES.confirmAccountsPanelTitle)}
      variant="md"
      primaryAction={{
        content: intl.formatMessage(MESSAGES.confirmAccountsSaveButton),
        onAction: handleSave,
        disabled: saving || !hasSelectedAccount(selectedAccounts),
        loading: saving,
      }}
    >
      <Flex gap="8" direction="column">
        <Text>{intl.formatMessage(MESSAGES.confirmAccountsPanelPrimaryDescription)}</Text>
        <Flex direction="column" gap="8">
          {bankConnections.map((connection) => (
            <Flex key={connection.id} direction="column" gap="1">
              <Text fontWeight="semiBold">{connection.institutionName}</Text>
              {connection.accounts.map((account) => (
                <CheckboxField
                  key={`select-account-${account.id}-field`}
                  name={`select-account-${account.id}-field`}
                  size="small"
                  options={{
                    name: `select-account-${account.id}`,
                    label: account.mask
                      ? `${account.name} - ${account.type} - *******${account.mask}`
                      : `${account.name} - ${account.type}`,
                    checked: selectedAccounts.connections[connection.id]?.accounts[account.id]?.enabled ?? false,
                    onChange: (checked: boolean) => {
                      dispatchSelectedAccounts({ connection, account, selected: checked });
                    },
                  }}
                />
              ))}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </SidePanel>
  );
};
